/**
 * Content Studio — Omni-channel content optimizer.
 * Shows platform-specific content across LinkedIn, Twitter/X, Instagram, TikTok, Facebook, YouTube.
 * Accessible from /content-studio or launched from AutoStrategy.
 * Supports 1-click channel optimization + scheduling.
 */
import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
  Sparkles, Copy, Check, RefreshCw, Calendar, Send, ChevronDown,
  ChevronUp, Zap, Hash, Clock, Eye, Globe, ArrowLeft, Link, Image,
} from 'lucide-react';
import ModelSelector from '../components/ModelSelector';
import SnapshotBadge from '../components/SnapshotBadge';

const API = process.env.REACT_APP_BACKEND_URL;

const CHANNELS = [
  {
    id: 'linkedin',
    label: 'LinkedIn',
    color: '#0A66C2',
    bg: 'rgba(10,102,194,0.12)',
    border: 'rgba(10,102,194,0.3)',
    charLimit: 3000,
    contentType: 'article',
    tips: ['Professional tone works best', 'Use 3-5 hashtags', 'Hook in first 2 lines', 'Tag thought leaders'],
  },
  {
    id: 'twitter',
    label: 'Twitter / X',
    color: '#1DA1F2',
    bg: 'rgba(29,161,242,0.1)',
    border: 'rgba(29,161,242,0.25)',
    charLimit: 280,
    contentType: 'thread',
    tips: ['Tweet threads get 3x more engagement', 'Lead with a bold claim', 'Use line breaks', 'End with a CTA question'],
  },
  {
    id: 'instagram',
    label: 'Instagram',
    color: '#E1306C',
    bg: 'rgba(225,48,108,0.1)',
    border: 'rgba(225,48,108,0.25)',
    charLimit: 2200,
    contentType: 'caption',
    tips: ['First sentence = the hook', '5-10 strategic hashtags', 'Emojis improve readability', 'Story + CTA format'],
  },
  {
    id: 'tiktok',
    label: 'TikTok',
    color: '#69C9D0',
    bg: 'rgba(105,201,208,0.1)',
    border: 'rgba(105,201,208,0.25)',
    charLimit: 2200,
    contentType: 'video_script',
    tips: ['Hook in 1-3 seconds is critical', 'Use trending sounds/music', 'Keep it 30-60 seconds', 'Text overlay = key insight'],
  },
  {
    id: 'facebook',
    label: 'Facebook',
    color: '#1877F2',
    bg: 'rgba(24,119,242,0.1)',
    border: 'rgba(24,119,242,0.25)',
    charLimit: 63206,
    contentType: 'post',
    tips: ['Questions drive engagement', 'Videos get highest reach', 'Tag relevant pages', 'Post during peak hours'],
  },
  {
    id: 'youtube',
    label: 'YouTube',
    color: '#FF0000',
    bg: 'rgba(255,0,0,0.08)',
    border: 'rgba(255,0,0,0.25)',
    charLimit: 5000,
    contentType: 'description',
    tips: ['First 125 chars = snippet', 'Include chapters', 'Keywords in first 3 lines', 'CTA to subscribe early'],
  },
];

function ChannelCard({ channel, content, snapshot, onGenerate, loading, topic }) {
  const [copied, setCopied] = useState(false);
  const [open, setOpen] = useState(true);
  const [scheduled, setScheduled] = useState(false);

  const mainText = content?.content || content?.tweet || content?.post || content?.script || content?.caption || content?.description || content?.thread || '';
  const charCount = mainText.length;
  const isOverLimit = charCount > channel.charLimit;

  const copy = () => {
    const full = [content?.title, mainText, (content?.hashtags || []).join(' ')].filter(Boolean).join('\n\n');
    navigator.clipboard.writeText(full);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const schedulePost = async () => {
    // For Twitter/X channel — try direct publish via Twitter API
    if (channel.id === 'twitter') {
      try {
        const { data } = await axios.post(`${API}/api/twitter/tweet`, {
          text: (mainText + '\n\n' + (content?.hashtags || []).slice(0, 5).join(' ')).slice(0, 280),
          thread: mainText.length > 280,
        });
        if (data.success) {
          setScheduled(true);
          setTimeout(() => setScheduled(false), 4000);
          return;
        }
      } catch (e) {
        // Fall through to scheduler if Twitter API fails
        console.warn('Twitter direct post failed, falling back to scheduler');
      }
    }
    try {
      await axios.post(`${API}/api/scheduler/create`, {
        platform: channel.label,
        content_type: channel.contentType,
        title: content?.title || topic || 'Scheduled post',
        content: mainText,
        scheduled_at: new Date(Date.now() + 24 * 3600 * 1000).toISOString(),
        brand_id: 'default',
      });
      setScheduled(true);
      setTimeout(() => setScheduled(false), 3000);
    } catch {
      alert('Failed to schedule post');
    }
  };

  return (
    <div className="rounded-2xl overflow-hidden transition-all"
      style={{ background: 'var(--llmc-surface)', border: `1px solid ${content ? channel.border : 'var(--llmc-border)'}` }}>
      {/* Channel header */}
      <div className="flex items-center justify-between px-4 py-3 cursor-pointer"
        style={{ background: content ? channel.bg : 'transparent' }}
        onClick={() => setOpen(o => !o)}>
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-xl flex items-center justify-center font-black text-xs"
            style={{ background: channel.bg, border: `1px solid ${channel.border}`, color: channel.color }}>
            {channel.label.slice(0, 2)}
          </div>
          <div>
            <div className="text-sm font-bold text-white">{channel.label}</div>
            <div className="text-[10px]" style={{ color: 'rgba(255,255,255,0.4)' }}>
              {channel.contentType} · {channel.charLimit.toLocaleString()} char limit
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {content && <div className="w-2 h-2 rounded-full" style={{ background: '#3dffa0' }} />}
          {loading ? <RefreshCw size={13} className="animate-spin" style={{ color: 'rgba(255,255,255,0.3)' }} /> :
            open ? <ChevronUp size={14} style={{ color: 'rgba(255,255,255,0.3)' }} /> :
              <ChevronDown size={14} style={{ color: 'rgba(255,255,255,0.3)' }} />}
        </div>
      </div>

      {open && (
        <div className="px-4 pb-4">
          {loading ? (
            <div className="flex items-center justify-center py-8 gap-3">
              <RefreshCw size={16} className="animate-spin" style={{ color: channel.color }} />
              <span className="text-xs" style={{ color: 'rgba(255,255,255,0.5)' }}>Optimizing for {channel.label}...</span>
            </div>
          ) : content ? (
            <div className="space-y-3">
              {/* Title */}
              {content.title && (
                <div className="text-sm font-bold text-white">{content.title}</div>
              )}
              {/* Hook */}
              {content.hook && (
                <div className="px-3 py-2 rounded-xl text-xs" style={{ background: `${channel.color}15`, border: `1px solid ${channel.color}30`, color: '#e8e8f0' }}>
                  <span className="text-[9px] font-bold uppercase mr-2" style={{ color: channel.color }}>Hook:</span>
                  {content.hook}
                </div>
              )}
              {/* Content body */}
              {mainText && (
                <div className="text-xs leading-relaxed whitespace-pre-wrap rounded-xl p-3"
                  style={{ background: 'rgba(255,255,255,0.03)', color: '#d0d0e0', maxHeight: 200, overflowY: 'auto' }}>
                  {mainText}
                </div>
              )}
              {/* Hashtags */}
              {content.hashtags?.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {content.hashtags.slice(0, 8).map((h, i) => (
                    <span key={i} className="text-[10px] px-1.5 py-0.5 rounded-full"
                      style={{ background: `${channel.color}12`, color: channel.color }}>{h}</span>
                  ))}
                </div>
              )}
              {/* Char count */}
              <div className="flex items-center justify-between">
                <div className="text-[10px]" style={{ color: isOverLimit ? '#f97316' : 'rgba(255,255,255,0.3)' }}>
                  {charCount.toLocaleString()} / {channel.charLimit.toLocaleString()} chars
                  {isOverLimit && ' · over limit'}
                </div>
                <div className="flex items-center gap-1.5">
                  <button onClick={copy}
                    className="flex items-center gap-1 px-2.5 py-1.5 rounded-xl text-[11px] font-semibold hover:opacity-80 transition-all"
                    style={{ background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.5)' }}>
                    {copied ? <><Check size={11} style={{ color: '#3dffa0' }} /> Copied</> : <><Copy size={11} /> Copy</>}
                  </button>
                  <button onClick={schedulePost}
                    className="flex items-center gap-1 px-2.5 py-1.5 rounded-xl text-[11px] font-semibold hover:opacity-80 transition-all"
                    style={{ background: scheduled ? 'rgba(61,255,160,0.12)' : `${channel.color}15`, border: `1px solid ${scheduled ? 'rgba(61,255,160,0.3)' : channel.border}`, color: scheduled ? '#3dffa0' : channel.color }}>
                    {scheduled ? <><Check size={11} /> Scheduled</> : <><Calendar size={11} /> Schedule</>}
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              {/* Tips */}
              <div className="grid grid-cols-2 gap-1.5 mb-3">
                {channel.tips.map((t, i) => (
                  <div key={i} className="text-[10px] px-2 py-1.5 rounded-lg flex items-start gap-1.5"
                    style={{ background: 'rgba(255,255,255,0.03)', color: 'rgba(255,255,255,0.4)' }}>
                    <Zap size={9} className="flex-shrink-0 mt-0.5" style={{ color: channel.color }} />
                    {t}
                  </div>
                ))}
              </div>
              <button onClick={() => onGenerate(channel.id)}
                disabled={!topic || loading}
                className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs font-bold transition-all hover:opacity-80 disabled:opacity-40"
                style={{ background: channel.bg, border: `1px solid ${channel.border}`, color: channel.color }}>
                <Sparkles size={13} /> Generate for {channel.label}
              </button>
            </div>
          )}
          {/* Pipeline snapshot */}
          {snapshot && <div className="mt-2"><SnapshotBadge snapshot={snapshot} type="article" /></div>}
        </div>
      )}
    </div>
  );
}

export default function ContentStudio() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [topic, setTopic] = useState(() => searchParams.get('topic') || localStorage.getItem('studio_topic') || '');
  const [sourceContent, setSourceContent] = useState(() => searchParams.get('content') || localStorage.getItem('studio_content') || '');
  const [channelContent, setChannelContent] = useState({});
  const [channelSnapshots, setChannelSnapshots] = useState({});
  const [loadingChannels, setLoadingChannels] = useState({});
  const [generatingAll, setGeneratingAll] = useState(false);
  const [showInput, setShowInput] = useState(!topic);
  const [modelOverride, setModelOverride] = useState(null); // null = default judge model

  useEffect(() => {
    localStorage.removeItem('studio_topic');
    localStorage.removeItem('studio_content');
  }, []);

  const generateForChannel = useCallback(async (channelId) => {
    if (!topic) return;
    const channel = CHANNELS.find(c => c.id === channelId);
    if (!channel) return;

    setLoadingChannels(prev => ({ ...prev, [channelId]: true }));
    try {
      const { data } = await axios.post(`${API}/api/trends/auto-generate`, {
        topic,
        brand_id: 'default',
        platform: channel.label,
        content_type: channel.contentType,
        source_content: sourceContent || undefined,
        use_trending: false,
        ...(modelOverride ? { model_override: modelOverride } : {}),
      });
      setChannelContent(prev => ({ ...prev, [channelId]: data.content || {} }));
      if (data.snapshot) setChannelSnapshots(prev => ({ ...prev, [channelId]: data.snapshot }));
    } catch (e) {
      // Fallback: use blog/social-posts endpoint if topic is title + content
      if (sourceContent) {
        try {
          const { data } = await axios.post(`${API}/api/blog/social-posts`, {
            title: topic,
            content: sourceContent,
            industry: 'AI & Technology',
          });
          const mapping = { linkedin: data.linkedin, twitter: data.twitter, instagram: data.instagram, facebook: data.facebook };
          const text = mapping[channelId] || '';
          if (text) setChannelContent(prev => ({ ...prev, [channelId]: { content: text } }));
        } catch {}
      }
    }
    setLoadingChannels(prev => ({ ...prev, [channelId]: false }));
  }, [topic, sourceContent]);

  const generateAll = async () => {
    if (!topic) return;
    setGeneratingAll(true);
    await Promise.allSettled(CHANNELS.map(c => generateForChannel(c.id)));
    setGeneratingAll(false);
  };

  return (
    <div className="flex-1 flex flex-col overflow-hidden" style={{ background: 'var(--llmc-bg)' }}>
      {/* Header */}
      <div className="p-5 flex-shrink-0" style={{ borderBottom: '1px solid var(--llmc-border)' }}>
        <div className="flex items-center gap-3 mb-3">
          <button onClick={() => navigate(-1)} className="p-2 rounded-xl hover:opacity-80"
            style={{ background: 'rgba(255,255,255,0.05)', color: 'rgba(255,255,255,0.4)' }}>
            <ArrowLeft size={16} />
          </button>
          <div className="w-9 h-9 rounded-xl flex items-center justify-center"
            style={{ background: 'linear-gradient(135deg, rgba(124,94,250,0.3), rgba(61,255,160,0.15))', border: '1px solid rgba(124,94,250,0.4)' }}>
            <Globe size={18} style={{ color: '#a68eff' }} />
          </div>
          <div>
            <h1 className="text-lg font-black text-white">Content Studio</h1>
            <p className="text-[11px]" style={{ color: 'var(--llmc-text-muted)' }}>Omni-channel content optimizer · 6 platforms · 1 topic</p>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <button onClick={() => setShowInput(s => !s)} className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold hover:opacity-80"
              style={{ background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.5)', border: '1px solid rgba(255,255,255,0.08)' }}>
              {showInput ? <ChevronUp size={12} /> : <ChevronDown size={12} />} Topic
            </button>
            <button onClick={generateAll} disabled={generatingAll || !topic}
              data-testid="generate-all-channels-btn"
              className="flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold transition-all hover:opacity-80 disabled:opacity-40"
              style={{ background: 'linear-gradient(135deg, #7c5efa, #a68eff)', color: '#fff' }}>
              {generatingAll ? <RefreshCw size={12} className="animate-spin" /> : <Sparkles size={12} />}
              {generatingAll ? 'Generating All...' : 'Generate All Channels'}
            </button>
            <ModelSelector lab="studio" onChange={setModelOverride} />
          </div>
        </div>

        {/* Topic input */}
        {showInput && (
          <div className="space-y-3 pt-2">
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider block mb-1" style={{ color: 'var(--llmc-text-muted)' }}>Topic / Title</label>
              <input value={topic} onChange={e => setTopic(e.target.value)}
                placeholder="e.g. How AI is transforming B2B sales in 2026" data-testid="studio-topic-input"
                className="llmc-input w-full px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider block mb-1" style={{ color: 'var(--llmc-text-muted)' }}>Source Content (optional — paste your blog post)</label>
              <textarea value={sourceContent} onChange={e => setSourceContent(e.target.value)}
                placeholder="Paste existing content here to repurpose across channels..."
                rows={3} data-testid="studio-content-input"
                className="llmc-input w-full px-3 py-2 text-xs resize-none" />
            </div>
          </div>
        )}

        {/* Active topic banner */}
        {topic && !showInput && (
          <div className="flex items-center gap-3 px-4 py-2.5 rounded-xl"
            style={{ background: 'rgba(124,94,250,0.08)', border: '1px solid rgba(124,94,250,0.2)' }}>
            <Hash size={12} style={{ color: '#a68eff' }} />
            <span className="text-sm font-semibold text-white flex-1 truncate">{topic}</span>
            <div className="flex items-center gap-1">
              {Object.keys(channelContent).length > 0 && (
                <span className="text-[10px] px-2 py-0.5 rounded-full"
                  style={{ background: 'rgba(61,255,160,0.1)', color: '#3dffa0' }}>
                  {Object.keys(channelContent).length}/{CHANNELS.length} generated
                </span>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Channel grid */}
      <div className="flex-1 overflow-y-auto p-5">
        {!topic ? (
          <div className="flex flex-col items-center justify-center h-full space-y-4">
            <div className="w-16 h-16 rounded-2xl flex items-center justify-center" style={{ background: 'rgba(124,94,250,0.1)' }}>
              <Globe size={32} style={{ color: '#a68eff' }} />
            </div>
            <div className="text-center">
              <div className="text-sm font-bold text-white mb-1">Enter a topic to get started</div>
              <div className="text-xs" style={{ color: 'var(--llmc-text-muted)' }}>Enter your content topic above and generate optimized posts for all 6 platforms</div>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            {CHANNELS.map(channel => (
              <ChannelCard
                key={channel.id}
                channel={channel}
                content={channelContent[channel.id]}
                snapshot={channelSnapshots[channel.id]}
                loading={loadingChannels[channel.id]}
                onGenerate={generateForChannel}
                topic={topic}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
